import * as React from 'react';
import { Popover as PopoverPrimitive } from 'radix-ui';

import { cn } from '../../lib/utils';

/** Keeps popover surfaces inside the viewport on narrow screens. */
export const POPOVER_VIEWPORT_MAX_WIDTH_CLASS = 'max-w-[calc(100vw-2rem)]';

/** Width that shrinks to the collision-safe space Radix computed for the trigger (mobile only). */
export const POPOVER_MOBILE_AVAILABLE_WIDTH_CLASS =
  'max-sm:w-[var(--radix-popover-content-available-width)]';

/**
 * Literal class per supported width: Tailwind's scanner only emits rules for
 * classes it can see verbatim in source, so a class interpolated from the
 * width argument would silently produce no CSS and leave the popover
 * uncapped. Add a new width by adding a literal entry here.
 */
const POPOVER_BOUNDED_MAX_WIDTH_CLASSES = {
  '14rem': 'max-w-[min(14rem,calc(100vw-2rem))]',
  '20rem': 'max-w-[min(20rem,calc(100vw-2rem))]',
  '22rem': 'max-w-[min(22rem,calc(100vw-2rem))]',
  '24rem': 'max-w-[min(24rem,calc(100vw-2rem))]',
  '28rem': 'max-w-[min(28rem,calc(100vw-2rem))]',
} as const;

export type PopoverBoundedMaxWidth = keyof typeof POPOVER_BOUNDED_MAX_WIDTH_CLASSES;

/** Cap a design max-width against the viewport, e.g. `popoverBoundedMaxWidthClass('20rem')`. */
export function popoverBoundedMaxWidthClass(width: PopoverBoundedMaxWidth) {
  return POPOVER_BOUNDED_MAX_WIDTH_CLASSES[width];
}

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  collisionPadding = 16,
  'aria-label': ariaLabel = 'Popover',
  container,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
  container?: React.ComponentProps<typeof PopoverPrimitive.Portal>['container'];
}) {
  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        data-lenis-prevent
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        aria-label={ariaLabel}
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 min-w-0 origin-[--radix-popover-content-transform-origin] overflow-x-hidden rounded-md border p-4 shadow-md outline-hidden',
          POPOVER_VIEWPORT_MAX_WIDTH_CLASS,
          'max-h-[var(--radix-popover-content-available-height)]',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="popover-header"
      className={cn('flex flex-col gap-1 text-sm', className)}
      {...props}
    />
  );
}

function PopoverTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return <div data-slot="popover-title" className={cn('font-medium', className)} {...props} />;
}

function PopoverDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="popover-description"
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
  PopoverHeader,
  PopoverTitle,
  PopoverDescription,
};
